"use client";

import { FC, MouseEvent, useState } from "react";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";

import { useAppContext } from "@/context/AppContextProvider";
import { useUserGetProfile } from "@/hooks/user/useUserGetProfile";
import CrownActiveIcon from "@/components//icons/CrownActiveIcon";
import CrownDeactiveIcon from "@/components/icons/CrownDeactiveIcon";
import PlusDivactiveIcon from "./icons/PlusDivactiveIcon";
import { URLConstants } from "@/constants/URLConstants";
import UserActivLink from "./mypage/UserActivLink";
import UserDeActiveLink from "./mypage/UserDeActiveLink";
import Link from "next/link";
import HomeLink from "./home/link/HomeLink";
import { GroupFooterLink } from "./group/links/GroupFooterLink";
import "@/components/footer.css";
import { useSession } from "next-auth/react";
import { GroupDataResponse } from "@/restapi/group";
import { useTranslation } from "@/lib/react/i18n";
import ConfrimModal from "./common/modal/ConfrimModal";
import PlusActiveIcon from "./icons/PlusActiveIcon";

// dynamic import に変更して SSR を無効する
const ImageUploadModal = dynamic(() => import("@/components/pict/ImageUploadModal"), {
  ssr: false,
});

const UploadModal: FC<{ group?: GroupDataResponse }> = ({ group }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data: session } = useSession();
  const userUuid = session?.user?.id ?? '';
  const { data: userProfile } = useUserGetProfile({ userUuid });

  const onClose = () => {
    setIsModalOpen(false);
  };

  if (userProfile?.postRestricted) {
    const baseMessage = t("Your posting is currently restricted.");
    const reasonLabel = t("Reason:");
    const contactMessage = t("Please contact the administrator for details.");
    const reason = userProfile.postRestrictionReason;
    const message = reason
      ? `${baseMessage}\n\n${reasonLabel} ${reason}`
      : `${baseMessage}\n\n${contactMessage}`;

    return (
      <div
        id="uploadModalButton"
        className="cursor-pointer"
        onClick={() => setIsModalOpen(true)}
      >
        <ConfrimModal
          isOpen={isModalOpen}
          onClose={onClose}
          title={t("Post restricted")}
          message={message}
        />
        {isModalOpen ? <PlusActiveIcon className="w-6 h-6" /> : <PlusDivactiveIcon className="w-6 h-6" />}
      </div>
    );
  }

  if (group) {
    if (group.isBeforeEntryPeriod) {
      return (
        <div
          id="uploadModalButton"
          className="cursor-pointer"
          onClick={() => setIsModalOpen(true)}
        >
          <ConfrimModal
            isOpen={isModalOpen}
            onClose={onClose}
            title={t("Photo submissions not yet accepted")}
            message={t("Submissions are not open yet.")}
          />
          {isModalOpen ? <PlusActiveIcon className="w-6 h-6" /> : <PlusDivactiveIcon className="w-6 h-6" />}
        </div>
      );
    } else if (group.isContestClosed) {
      return (
        <div
          id="uploadModalButton"
          className="cursor-pointer"
          onClick={() => setIsModalOpen(true)}
        >
          <ConfrimModal
            isOpen={isModalOpen}
            onClose={onClose}
            title={t("Contest ends")}
            message={t(
              "This contest has ended. Photo submissions and score input are no longer available."
            )}
          />
          {isModalOpen ? <PlusActiveIcon className="w-6 h-6" /> : <PlusDivactiveIcon className="w-6 h-6" />}
        </div>
      );
    } else if (group.isInContestPeriod) {
      return (
        <div
          id="uploadModalButton"
          className="cursor-pointer"
          onClick={() => setIsModalOpen(true)}
        >
          <ConfrimModal
            isOpen={isModalOpen}
            onClose={onClose}
            title={t("Photo submissions closed")}
            message={t(
              "Photo submissions for this contest are currently closed. Only scoring is available."
            )}
          />
          {isModalOpen ? <PlusActiveIcon className="w-6 h-6" /> : <PlusDivactiveIcon className="w-6 h-6" />}
        </div>
      );
    }
  }

  return <ImageUploadModal group={group} />;
};

const Footer = ({ group }: { group?: GroupDataResponse }) => {
  const { setSettingMenuProps, visableMode, isMobile, isModalOpen, setScreenType } = useAppContext();
  const [isImgHistoryHovered, setIsImgHistoryHovered] = useState(false);
  const [isCrownHovered, setIsCrownHovered] = useState(false);
  const pathname = usePathname();
  const { data: session } = useSession();
  const user = session?.user;

  const handleClick = (
    e: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>,
    url: string,
    callback?: () => void
  ) => {
    e.preventDefault();
    e.stopPropagation();

    setSettingMenuProps({
      isMeneuOpen: false,
    });

    if (typeof window !== "undefined") {
      window.location.href = url;
    }
  };

  return (
    <div
      className={`bg-black fixed bottom-0 left-1/2 transform -translate-x-1/2 w-full min-w-screen-sm wrapFootNaviArea ${visableMode.isShow ? "fade-in" : "fade-out"
        } min-h-[60px] z-[100]`}
    >
      <ul className="flex justify-around bg-opacity-50 py-4 z-50 footNaviArea">
        <li className="flex flex-col items-center footNavi footNavi01">
          <HomeLink />
        </li>
        <li className="flex flex-col items-center footNavi footNavi02">
          <a
            href={`${URLConstants.user.mypage.pathname}`}
            className={`${isModalOpen ? "linkDisabled" : "cursor-pointer"}`}
            onMouseEnter={() => setIsImgHistoryHovered(true)}
            onMouseLeave={() => setIsImgHistoryHovered(false)}
          >
            {URLConstants.user.mypage.pathname === pathname ||
              (isImgHistoryHovered && !isMobile) ? (
              <UserActivLink />
            ) : (
              <UserDeActiveLink />
            )}
          </a>
        </li>

        <li className="flex flex-col items-center footNavi footNavi03">
          {user ? (
            <UploadModal group={group} />
          ) : (
            <a
              className={`${isModalOpen ? "linkDisabled" : "cursor-pointer"}`}
              href={URLConstants.accessDenied.pathname}
            >
              <PlusDivactiveIcon className="w-6 h-6" />
            </a>
          )}
        </li>
        <li className="flex flex-col items-center footNavi footNavi04">
          <GroupFooterLink isModalOpen={isModalOpen} user={user} />
        </li>
        <li className="flex flex-col items-center  footNavi footNavi05">
          <Link
            onClick={(e) => handleClick(e, URLConstants.ranking.pathname, () => setScreenType("grid"))}
            href={URLConstants.ranking.pathname}
            className={`${isModalOpen ? "linkDisabled" : "cursor-pointer"}`}
            onMouseEnter={() => setIsCrownHovered(true)}
            onMouseLeave={() => setIsCrownHovered(false)}
          >
            {URLConstants.ranking.pathname === pathname || (isCrownHovered && !isMobile) ? (
              <CrownActiveIcon />
            ) : (
              <CrownDeactiveIcon />
            )}
          </Link>
        </li>
      </ul>
    </div>
  );
};

export default Footer;